import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Spinner, Button } from "react-bootstrap";
import toastr from "toastr";
import { ArrowUp, ArrowDown, Search, Download } from "lucide-react";

interface ExpenseTypeRow {
    accountType: string;
    code: string;
    shortName: string;
    description: string;
    parentExpenseType: string;
    nonTaxable: boolean;
    isActive: boolean;
}

const PARENT_TOOLTIP = "";

type SortKey = keyof ExpenseTypeRow;
type SortDir = "asc" | "desc";

const MasterExpenseTypeList: React.FC = () => {
    const [rows, setRows] = useState<ExpenseTypeRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [accountTypeFilter, setAccountTypeFilter] = useState<string>("ALL");
    const [sortKey, setSortKey] = useState<SortKey>("accountType");
    const [sortDir, setSortDir] = useState<SortDir>("asc");

    useEffect(() => {
        axios
            .get("/api/Database/expense-types")
            .then((res) => {
                const data = (res.data || []).map((x: any) => ({
                    accountType: x.accountType,
                    code: x.code,
                    shortName: x.shortName,
                    description: x.description,
                    parentExpenseType: x.parentExpenseType,
                    nonTaxable: x.nonTaxable,
                    isActive: x.isActive,
                }));
                setRows(data);
            })
            .catch((err) => {
                toastr.error("Failed to load expense types", "Error");
            })
            .finally(() => setLoading(false));
    }, []);

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    };

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        let list = rows;
        if (accountTypeFilter !== "ALL") {
            list = list.filter((r) => r.accountType === accountTypeFilter);
        }
        if (term) {
            list = list.filter(
                (r) =>
                    (r.code || "").toLowerCase().includes(term) ||
                    (r.shortName || "").toLowerCase().includes(term) ||
                    (r.description || "").toLowerCase().includes(term) ||
                    (r.accountType || "").toLowerCase().includes(term) ||
                    (r.parentExpenseType || "").toLowerCase().includes(term)
            );
        }
        const compare = (av: any, bv: any, dir: SortDir) => {
            if (typeof av === "boolean" && typeof bv === "boolean") {
                return av === bv ? 0 : (av ? -1 : 1) * (dir === "asc" ? 1 : -1);
            }
            const as = (av ?? "").toString().toLowerCase();
            const bs = (bv ?? "").toString().toLowerCase();
            if (as < bs) return dir === "asc" ? -1 : 1;
            if (as > bs) return dir === "asc" ? 1 : -1;
            return 0;
        };
        const sorted = [...list].sort((a, b) => {
            const primary = compare(a[sortKey], b[sortKey], sortDir);
            if (primary !== 0) return primary;
            if (sortKey !== "shortName") {
                return compare(a.shortName, b.shortName, "asc");
            }
            return 0;
        });
        return sorted;
    }, [rows, search, accountTypeFilter, sortKey, sortDir]);

    const exportCsv = () => {
        const headers = ["Type", "Code", "Parent Expense Type", "Name", "Description", "Non Taxable"];
        const escape = (val: any) => {
            const s = (val ?? "").toString();
            if (/[",\r\n]/.test(s)) {
                return `"${s.replace(/"/g, '""')}"`;
            }
            return s;
        };
        const lines = [headers.join(",")];
        filtered.forEach((r) => {
            const nonTaxable = r.accountType === "LSA" ? (r.nonTaxable ? "Non Taxable" : "Taxable") : "";
            lines.push([
                escape(r.accountType),
                escape(r.code),
                escape(r.parentExpenseType),
                escape(r.shortName),
                escape(r.description),
                escape(nonTaxable),
            ].join(","));
        });
        const csv = "﻿" + lines.join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `master-expense-types-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const SortHeader = ({ label, column, width }: { label: string; column: SortKey; width?: number | string }) => (
        <th style={{ cursor: "pointer", userSelect: "none", width }} onClick={() => toggleSort(column)}>
            {label}
            {sortKey === column && (
                <span style={{ marginLeft: 4 }}>
                    {sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                </span>
            )}
        </th>
    );

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: 40 }}>
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                <h4 style={{ margin: 0, flexGrow: 1 }}>Master Expense Type List</h4>
                <div style={{ position: "relative" }}>
                    <Search size={16} style={{ position: "absolute", left: 10, top: 10, color: "#888" }} />
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ paddingLeft: 32, width: 240 }}
                    />
                </div>
                <select
                    className="form-select"
                    style={{ width: 140 }}
                    value={accountTypeFilter}
                    onChange={(e) => setAccountTypeFilter(e.target.value)}
                >
                    <option value="ALL">All types</option>
                    <option value="HSA">HSA</option>
                    <option value="LSA">LSA</option>
                </select>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={exportCsv}
                    disabled={filtered.length === 0}
                    title="Export current view to CSV"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                    <Download size={14} />
                    CSV
                </Button>
            </div>

            <div className="dashboard-table-container">
                <table className="table table-striped table-hover">
                    <thead>
                        <tr>
                            <SortHeader label="Type" column="accountType" />
                            <SortHeader label="Code" column="code" />
                            <th
                                style={{ cursor: "pointer", userSelect: "none", width: 220, textAlign: "center", position: "relative" }}
                                onClick={() => toggleSort("parentExpenseType")}
                                title={PARENT_TOOLTIP}
                            >
                                Parent Expense Type
                                {sortKey === "parentExpenseType" && (
                                    <span style={{ position: "absolute", marginLeft: 4 }}>
                                        {sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                                    </span>
                                )}
                            </th>
                            <SortHeader label="Name" column="shortName" width={100} />
                            <SortHeader label="Description" column="description" />
                            <SortHeader label="Non Taxable" column="nonTaxable" width={140} />
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={6} style={{ textAlign: "center", padding: 20 }}>
                                    No expense types match the current filters.
                                </td>
                            </tr>
                        ) : (
                            filtered.map((r, idx) => (
                                <tr key={idx}>
                                    <td>{r.accountType}</td>
                                    <td>{r.code}</td>
                                    <td style={{ textAlign: "center" }}>{r.parentExpenseType}</td>
                                    <td>{r.shortName}</td>
                                    <td style={{ whiteSpace: "pre-wrap" }}>{r.description}</td>
                                    <td>{r.accountType === "LSA" ? (r.nonTaxable ? "Non Taxable" : "Taxable") : ""}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <div style={{ marginTop: 8, color: "#666", fontSize: 13 }}>
                Showing {filtered.length} of {rows.length}
            </div>
        </div>
    );
};

export default MasterExpenseTypeList;
